/**
 * ContentCaptionSync — Dimus Video Components  v1.1.0
 *
 * Componente de legenda kinetic dual-stack:
 * ┌────────────────────────────────────────────────────────────┐
 * │  STACK A: @remotion/captions (createTikTokStyleCaptions)   │
 * │  STACK B: CaptionCraft / SRT word-level timestamps         │
 * └────────────────────────────────────────────────────────────┘
 *
 * LOOK CANÔNICO DIMUS (CaptionCraft v1.1.0):
 * Camada 1: rolling highlight palavra-por-palavra (karaokê — BASE)
 * Camada 2: UMA palavra de carga semântica por beat com tratamento extra
 *
 * Regras de design (VETO do dono 2026-05-30):
 * - NUNCA retângulo/scrim/bloom atrás do texto
 * - Proteção via text-shadow dark tight + glow no destaque
 * - MÁX. 2 famílias tipográficas por peça (1 base + 1 destaque)
 * - Safe-zone: lados 7% / topo 12% / base 18% (IG Reel 1080×1920)
 * - Posição variável mas organizada; default centro ou acima da cabeça
 *
 * FIXES v1.1.0 (alinhamento com CaptionCraft v1.5.0):
 * ┌──────────────────────────────────────────────────────────────┐
 * │ FIX 1 — Contraste adaptativo (bgMode dark/light)            │
 * │   dark: rgba(255,255,255,0.55) base + #00DF82 highlight     │
 * │   light: #16171C grafite + #0a9e5e verde escurecido          │
 * │                                                              │
 * │ FIX 2 — Auto-fit (font scaling)                             │
 * │   Reduz fontSize se estimativa de largura > 86% do container │
 * │   Considera overshoot de scale-pop 1.06x                    │
 * │                                                              │
 * │ FIX 3 — Anti-jitter baseline                                │
 * │   Container com altura fixa = emphasisFontSize * 1.4        │
 * │   alignItems center → baseline visual estável entre chunks  │
 * └──────────────────────────────────────────────────────────────┘
 *
 * ADR: ia-skills ADR-0001
 * Integração: skillcontentosdimus-captioncraft v1.5.0
 */
import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Easing } from "remotion";

// ── Easings ──────────────────────────────────────────────────────────────────
const ease = {
  expoOut: Easing.bezier(0.19, 1, 0.22, 1),
  backOut:  Easing.bezier(0.34, 1.56, 0.64, 1),
};

// ── Type: Stack A — @remotion/captions format ────────────────────────────────
export interface RemotionCaption {
  text:    string;
  startMs: number;
  endMs:   number;
}

// ── Type: Stack B — CaptionCraft / SRT word-level ────────────────────────────
export interface WordTimestamp {
  word:      string;
  start:     number;   // seconds
  end:       number;   // seconds
  emphasis?: boolean;  // camada 2 — máx 1-2 por clipe de 30s
}

// ── Presets ───────────────────────────────────────────────────────────────────
export type CaptionPreset =
  | "editorial-dimus"      // Cormorant italic + Coolvetica — registro editorial
  | "bold-center-pop"      // Coolvetica bold — karaokê 2026 (padrão)
  | "neon-glow-flicker"    // Humane condensed — hook dramático
  | "humane-statement"     // Humane display — uma frase por beat
  | "coolvetica-friendly"  // Coolvetica — conversacional
  | "custom";              // usa props diretas

export type EmphasisAnimation = "scale-pop" | "color-flash" | "blur-reveal" | "none";

export type CaptionPosition =
  | "center"          // centro da safe zone
  | "above-subject"   // 15% abaixo do topo da safe zone
  | "bottom-third"    // último terço da safe zone
  | "custom";

// ── FIX 1: background mode para contraste adaptativo ─────────────────────────
// dark: white base (padrão ContentReel dark bg)
// light: grafite #16171C + verde escurecido (Paper/Glass family)
export type BgMode = "dark" | "light";

// ── Main Props ────────────────────────────────────────────────────────────────
export interface ContentCaptionSyncProps {
  // INPUT — escolha um dos dois
  captions?: RemotionCaption[];   // Stack A
  words?:    WordTimestamp[];     // Stack B

  // STYLING
  preset?:          CaptionPreset;
  highlightColor?:  string;
  baseColor?:       string;
  emphasisColor?:   string;
  fontFamily?:      string;
  emphasisFont?:    string;
  fontSize?:        number;
  emphasisFontSize?: number;
  fontWeight?:      number;
  emphasisAnimation?: EmphasisAnimation;

  // FIX 1 — contraste adaptativo
  /** "dark" = base branco (default, ContentReel dark bg)
   *  "light" = base grafite #16171C (Paper/Glass family) */
  bgMode?: BgMode;

  // LAYOUT
  position?:          CaptionPosition;
  customTop?:         number;
  paddingHorizontal?: number;
  wordsPerLine?:      number;
  textAlign?:         "center" | "left" | "right";

  // TIMING
  startFrame?:  number;

  // DEBUG
  debugSafeZone?: boolean;
}

// ── Safe zone constants (IG Reel 1080×1920) ───────────────────────────────────
const SAFE = {
  top:    290,   // 15% reservado para IG chrome
  bottom: 1500,  // bottom 22% começa aqui
  left:   76,    // 7%
  right:  76,    // 7%
};

// ── Preset configs ────────────────────────────────────────────────────────────
interface PresetConfig {
  fontFamily:       string;
  emphasisFont:     string;
  fontSize:         number;
  emphasisFontSize: number;
  fontWeight:       number;
  highlightColor:   string;
  emphasisColor:    string;
  emphasisAnimation: EmphasisAnimation;
  textAlign:        "center" | "left" | "right";
}

const PRESETS: Record<Exclude<CaptionPreset, "custom">, PresetConfig> = {
  "editorial-dimus": {
    fontFamily:       "'Cormorant Garamond', 'Times New Roman', serif",
    emphasisFont:     "'Coolvetica', system-ui, sans-serif",
    fontSize:         72,
    emphasisFontSize: 88,
    fontWeight:       600,
    highlightColor:   "#00DF82",
    emphasisColor:    "#FECB18",
    emphasisAnimation:"blur-reveal",
    textAlign:        "left",
  },
  "bold-center-pop": {
    fontFamily:       "'Coolvetica', 'Helvetica Neue', system-ui, sans-serif",
    emphasisFont:     "'Humane', 'Impact', system-ui, sans-serif",
    fontSize:         80,
    emphasisFontSize: 96,
    fontWeight:       700,
    highlightColor:   "#00DF82",
    emphasisColor:    "#FECB18",
    emphasisAnimation:"scale-pop",
    textAlign:        "center",
  },
  "neon-glow-flicker": {
    fontFamily:       "'Humane', 'Impact', system-ui, sans-serif",
    emphasisFont:     "'Humane', 'Impact', system-ui, sans-serif",
    fontSize:         88,
    emphasisFontSize: 104,
    fontWeight:       900,
    highlightColor:   "#00DF82",
    emphasisColor:    "#34E2FF",
    emphasisAnimation:"color-flash",
    textAlign:        "center",
  },
  "humane-statement": {
    fontFamily:       "'Humane', 'Impact', system-ui, sans-serif",
    emphasisFont:     "'Humane', 'Impact', system-ui, sans-serif",
    fontSize:         96,
    emphasisFontSize: 112,
    fontWeight:       900,
    highlightColor:   "#fff",
    emphasisColor:    "#FECB18",
    emphasisAnimation:"scale-pop",
    textAlign:        "center",
  },
  "coolvetica-friendly": {
    fontFamily:       "'Coolvetica', system-ui, sans-serif",
    emphasisFont:     "'Coolvetica', system-ui, sans-serif",
    fontSize:         76,
    emphasisFontSize: 90,
    fontWeight:       700,
    highlightColor:   "#00DF82",
    emphasisColor:    "#FECB18",
    emphasisAnimation:"scale-pop",
    textAlign:        "center",
  },
};

// ── FIX 1: contraste adaptativo por bgMode ────────────────────────────────────
// CaptionCraft v1.5.0: fundo claro → grafite #16171C + verde escurecido #0a9e5e
// NUNCA scrim/plate — apenas trocar a cor do texto
const LIGHT_BG_OVERRIDES = {
  baseColor:      "#16171C",
  highlightColor: "#0a9e5e",  // verde escurecido para contraste ≥4.5:1 em bg claro
  textShadowBase: "0 2px 4px rgba(255,255,255,0.4)",  // sombra clara em bg claro
  textShadowActive: "0 2px 4px rgba(255,255,255,0.6), 0 0 8px",
};

const DARK_BG_DEFAULTS = {
  baseColor:      "rgba(255,255,255,0.55)",
  textShadowBase: "0 2px 5px rgba(0,0,0,.9)",
  textShadowActive: "0 2px 5px rgba(0,0,0,.9), 0 0",
};

// ── FIX 2: auto-fit font scaling ──────────────────────────────────────────────
// Heurística: char médio em Coolvetica/Humane ≈ 0.55 × fontSize (uppercase heavy)
// Considera overshoot de scale-pop cap 1.06x (CaptionCraft v1.4.0)
const AVG_CHAR_WIDTH_FACTOR = 0.55;
const SCALE_POP_CAP = 1.06;

function autoFitFontSize(
  chunk: WordTimestamp[],
  fontSize: number,
  availableWidth: number,
): number {
  if (chunk.length === 0) return fontSize;
  const text = chunk.map((w) => w.word).join(" ");
  const estimatedWidth = text.length * fontSize * AVG_CHAR_WIDTH_FACTOR * SCALE_POP_CAP;
  const maxWidth = availableWidth * 0.86;
  if (estimatedWidth <= maxWidth) return fontSize;
  const scaled = Math.floor(fontSize * (maxWidth / estimatedWidth));
  return Math.max(scaled, 44); // mínimo absoluto de 44px para legibilidade
}

// ── Stack A adapter: RemotionCaption[] → WordTimestamp[] ─────────────────────
function remotionCaptionsToWords(captions: RemotionCaption[]): WordTimestamp[] {
  const words: WordTimestamp[] = [];
  for (const cap of captions) {
    const wordList = cap.text.trim().split(/\s+/);
    const durPerWord = (cap.endMs - cap.startMs) / wordList.length / 1000;
    wordList.forEach((w, i) => {
      const start = cap.startMs / 1000 + i * durPerWord;
      words.push({ word: w, start, end: start + durPerWord });
    });
  }
  return words;
}

// ── AnimatedWord ──────────────────────────────────────────────────────────────
const AnimatedWord: React.FC<{
  word:              string;
  isActive:          boolean;
  isEmphasis:        boolean;
  baseColor:         string;
  highlightColor:    string;
  emphasisColor:     string;
  fontFamily:        string;
  emphasisFont:      string;
  fontSize:          number;    // já auto-fit
  emphasisFontSize:  number;    // proporcional ao fit
  fontWeight:        number;
  emphasisAnimation: EmphasisAnimation;
  bgMode:            BgMode;
  frame:             number;
  activeFrame:       number;
}> = ({
  word, isActive, isEmphasis,
  baseColor, highlightColor, emphasisColor,
  fontFamily, emphasisFont, fontSize, emphasisFontSize, fontWeight,
  emphasisAnimation, bgMode, frame, activeFrame,
}) => {
  const localSinceActive = frame - activeFrame;

  // Camada 1: cor de highlight
  const color        = isActive ? (isEmphasis ? emphasisColor : highlightColor) : baseColor;
  const currentFont  = isEmphasis && isActive ? emphasisFont : fontFamily;
  const currentSize  = isEmphasis && isActive ? emphasisFontSize : fontSize;

  // Camada 2: animation de ênfase
  let emphasisScale = 1;
  let emphasisBlur  = 0;
  let emphasisGlow  = "none";

  if (isActive && isEmphasis && emphasisAnimation !== "none") {
    switch (emphasisAnimation) {
      case "scale-pop": {
        const sp = spring({
          frame: Math.max(0, localSinceActive),
          fps: 30,
          config: { damping: 9, stiffness: 160 },
          from: 0.8,
          to: 1,
        });
        emphasisScale = sp;
        emphasisGlow  = `drop-shadow(0 0 12px ${emphasisColor})`;
        break;
      }
      case "blur-reveal": {
        emphasisBlur = interpolate(localSinceActive, [0, 12], [10, 0], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: ease.expoOut,
        });
        break;
      }
      case "color-flash": {
        const flicker = 0.7 + Math.sin(localSinceActive * 0.6) * 0.3;
        emphasisGlow = `drop-shadow(0 0 ${12 * flicker}px ${emphasisColor})`;
        break;
      }
      default: break;
    }
  }

  // FIX 1: text-shadow adaptativo por bgMode
  const isDark = bgMode === "dark";
  const shadowBase   = isDark ? DARK_BG_DEFAULTS.textShadowBase   : LIGHT_BG_OVERRIDES.textShadowBase;
  const shadowPrefix = isDark ? DARK_BG_DEFAULTS.textShadowActive  : LIGHT_BG_OVERRIDES.textShadowActive;
  const glowPx       = isEmphasis ? "18px" : "6px";
  const textShadow   = isActive
    ? `${shadowBase}, ${shadowPrefix} ${glowPx} ${color}66`
    : shadowBase;

  return (
    <span
      style={{
        display:       "inline-block",
        fontFamily:    currentFont,
        fontSize:      currentSize,
        fontWeight,
        color,
        transform:     `scale(${emphasisScale})`,
        filter:        emphasisBlur > 0 ? `blur(${emphasisBlur}px)` : emphasisGlow,
        textShadow,
        whiteSpace:    "pre",
        willChange:    "transform, filter, color",
        letterSpacing: "-0.02em",
      }}
    >
      {word}{" "}
    </span>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export const ContentCaptionSync: React.FC<ContentCaptionSyncProps> = ({
  captions,
  words: wordsProp,
  preset              = "bold-center-pop",
  highlightColor,
  baseColor,
  emphasisColor,
  fontFamily,
  emphasisFont,
  fontSize,
  emphasisFontSize,
  fontWeight,
  emphasisAnimation,
  bgMode              = "dark",
  position            = "center",
  customTop,
  paddingHorizontal   = 80,
  wordsPerLine        = 4,
  textAlign           = "center",
  startFrame          = 0,
  debugSafeZone       = false,
}) => {
  const frame            = useCurrentFrame();
  const { fps: videoFps } = useVideoConfig();

  // Resolve word list
  const words: WordTimestamp[] = captions
    ? remotionCaptionsToWords(captions)
    : wordsProp ?? [];

  // Resolve preset
  const resolvedPreset = preset !== "custom" ? PRESETS[preset] : null;

  const resolvedFontFamily  = fontFamily      ?? resolvedPreset?.fontFamily      ?? "'Coolvetica', system-ui, sans-serif";
  const resolvedEmphFont    = emphasisFont     ?? resolvedPreset?.emphasisFont    ?? resolvedFontFamily;
  const resolvedFontSize    = fontSize         ?? resolvedPreset?.fontSize        ?? 80;
  const resolvedEmphSize    = emphasisFontSize ?? resolvedPreset?.emphasisFontSize ?? resolvedFontSize + 16;
  const resolvedWeight      = fontWeight       ?? resolvedPreset?.fontWeight      ?? 700;
  const resolvedEmphAnim    = emphasisAnimation ?? resolvedPreset?.emphasisAnimation ?? "scale-pop";
  const resolvedTextAlign   = resolvedPreset?.textAlign ?? textAlign;

  // FIX 1: cor base adaptativa por bgMode
  const resolvedBase = baseColor ?? (bgMode === "light"
    ? LIGHT_BG_OVERRIDES.baseColor
    : DARK_BG_DEFAULTS.baseColor);

  const resolvedHlColor = highlightColor ?? (bgMode === "light"
    ? LIGHT_BG_OVERRIDES.highlightColor
    : resolvedPreset?.highlightColor ?? "#00DF82");

  const resolvedEmphColor = emphasisColor ?? resolvedPreset?.emphasisColor ?? "#FECB18";

  // Current time
  const currentSec = (frame - startFrame) / videoFps;

  // Active word
  const activeIdx = words.findIndex((w) => currentSec >= w.start && currentSec < w.end);

  // Current chunk
  const lineIdx    = activeIdx >= 0 ? Math.floor(activeIdx / wordsPerLine) : 0;
  const chunkStart = lineIdx * wordsPerLine;
  const chunkEnd   = Math.min(chunkStart + wordsPerLine, words.length);
  const chunk      = words.slice(chunkStart, chunkEnd);

  // FIX 2: auto-fit font size
  const canvasWidth      = 1080;
  const availableWidth   = canvasWidth - 2 * paddingHorizontal;
  const fittedFontSize   = autoFitFontSize(chunk, resolvedFontSize, availableWidth);
  // Manter proporção emph/base
  const emphRatio        = resolvedEmphSize / resolvedFontSize;
  const fittedEmphSize   = Math.round(fittedFontSize * emphRatio);

  // Position
  let topPx: number;
  switch (position) {
    case "center":
      topPx = Math.floor((SAFE.top + SAFE.bottom) / 2) - resolvedFontSize;
      break;
    case "above-subject":
      topPx = SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.15);
      break;
    case "bottom-third":
      topPx = SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.68);
      break;
    case "custom":
      topPx = customTop ?? SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.5);
      break;
    default:
      topPx = Math.floor((SAFE.top + SAFE.bottom) / 2) - resolvedFontSize;
  }

  // Chunk entrance animation (opacity only entry keeps baseline stable)
  const chunkEntryFrame = chunkStart < words.length
    ? Math.floor(words[chunkStart].start * videoFps) + startFrame
    : 0;
  const chunkLocal = Math.max(0, frame - chunkEntryFrame);

  // FIX 3 (parte 1): opacity-only entry → sem translateY → baseline não jita
  const chunkOp = interpolate(chunkLocal, [0, 8], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: ease.expoOut,
  });

  if (words.length === 0) return null;

  // FIX 3 (parte 2): container com altura fixa = emphasisFontSize * 1.4
  // alignItems: center → todas as palavras (regulares e emphasis) centram no mesmo eixo
  // → baseline visual estável entre chunks
  const fixedLineHeight = Math.round(fittedEmphSize * 1.4);

  return (
    <>
      {/* Debug overlay */}
      {debugSafeZone && (
        <div style={{
          position: "absolute",
          top: SAFE.top, left: SAFE.left, right: SAFE.right,
          bottom: 1920 - SAFE.bottom,
          border: "2px dashed rgba(255,0,0,0.4)",
          pointerEvents: "none",
        }} />
      )}

      {/* Caption container — posição fixa, altura fixa (FIX 3) */}
      <div
        style={{
          position:         "absolute",
          top:              topPx,
          left:             paddingHorizontal,
          right:            paddingHorizontal,
          height:           fixedLineHeight,       // FIX 3: height fixa
          display:          "flex",
          flexWrap:         "nowrap",              // FIX 3: sem quebra de linha inesperada
          alignItems:       "center",              // FIX 3: centrar verticalmente
          justifyContent:   resolvedTextAlign === "center"
            ? "center"
            : resolvedTextAlign === "right"
              ? "flex-end"
              : "flex-start",
          opacity:          chunkOp,               // FIX 3: só opacity, sem translateY
          willChange:       "opacity",
          overflow:         "hidden",              // FIX 2: garante não vaza
        }}
      >
        {chunk.map((w, i) => {
          const wordIdx   = chunkStart + i;
          const isActive  = wordIdx === activeIdx;
          const isEmphasis = Boolean(w.emphasis) && isActive;
          const activeFrame = isActive
            ? Math.floor(w.start * videoFps) + startFrame
            : 0;

          return (
            <AnimatedWord
              key={`${wordIdx}-${w.word}`}
              word={w.word}
              isActive={isActive}
              isEmphasis={isEmphasis}
              baseColor={resolvedBase}
              highlightColor={resolvedHlColor}
              emphasisColor={resolvedEmphColor}
              fontFamily={resolvedFontFamily}
              emphasisFont={resolvedEmphFont}
              fontSize={fittedFontSize}
              emphasisFontSize={fittedEmphSize}
              fontWeight={resolvedWeight}
              emphasisAnimation={resolvedEmphAnim}
              bgMode={bgMode}
              frame={frame}
              activeFrame={activeFrame}
            />
          );
        })}
      </div>
    </>
  );
};

// ── Usage Examples ────────────────────────────────────────────────────────────
/*
// Stack A — @remotion/captions (gerado pelo produce_reel.py via Whisper)
<ContentCaptionSync
  captions={captions}
  preset="bold-center-pop"
  position="center"
  bgMode="dark"          // dark bg (ContentReel padrão)
  startFrame={0}
/>

// Stack A — fundo claro (Paper/Glass family)
<ContentCaptionSync
  captions={captions}
  preset="editorial-dimus"
  position="above-subject"
  bgMode="light"         // adapta: grafite #16171C + verde #0a9e5e
/>

// Stack B — CaptionCraft word timestamps
const words: WordTimestamp[] = [
  { word: "seu",    start: 0.00, end: 0.18 },
  { word: "preço",  start: 0.19, end: 0.45, emphasis: true },
  { word: "mínimo", start: 0.46, end: 0.80 },
];
<ContentCaptionSync
  words={words}
  preset="bold-center-pop"
  position="above-subject"
  bgMode="dark"
  wordsPerLine={4}
  startFrame={0}
/>

// Debug safe zone
<ContentCaptionSync words={words} preset="bold-center-pop" debugSafeZone />
*/

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
